// Where is the caret, structurally?
//
// Autocomplete needs more than the word under the cursor. `=GI.SUM(a` should
// offer the project's FIELDS, not ABS/AND/AVERAGE — and the only way to know
// that is to know we are inside argument 0 of a GI call. So this scans the
// formula and reports the enclosing call, the argument index, and whether the
// caret sits inside a string literal.

export interface CallContext {
  /** Enclosing function name, uppercased. Empty at the top level. */
  fn: string;
  /** 0-based argument index within that call. */
  argIndex: number;
  /** Offset where the current argument starts (after `(` or `,`). */
  argStart: number;
  /** True when the caret is inside an unterminated double-quoted string. */
  quoted: boolean;
  /** Offset just after the opening quote. Only meaningful when `quoted`. */
  quoteStart: number;
  /** Raw text of the arguments already closed by a comma in this call. */
  args: string[];
  /**
   * Raw text of the arguments AFTER the one the caret is in, up to the call's
   * closing paren. The source is a GI call's last argument, so going back to
   * edit the field of `GI.TOTAL("gro|", "", "bakedSections")` has to see it.
   */
  after: string[];
}

interface Frame {
  fn: string;
  argIndex: number;
  argStart: number;
  args: string[];
}

/**
 * Scan left-to-right to the caret. A left-to-right pass is what makes quote
 * handling correct: scanning backwards cannot tell an opening quote from a
 * closing one, so `="a)b" & SUM(` would report the wrong call.
 */
export function callContextAt(text: string, caret: number): CallContext {
  const stack: Frame[] = [];
  let quoted = false;
  let quoteStart = 0;
  let topArgStart = 0;
  let i = 0;

  while (i < caret && i < text.length) {
    const ch = text[i]!;

    if (quoted) {
      if (ch === '"') {
        // `""` is an escaped quote inside the string, not the end of it.
        if (text[i + 1] === '"') {
          i += 2;
          continue;
        }
        quoted = false;
      }
      i += 1;
      continue;
    }

    if (ch === '"') {
      quoted = true;
      quoteStart = i + 1;
      i += 1;
      continue;
    }

    if (ch === '(') {
      // The function name is the identifier immediately before the paren.
      let j = i;
      while (j > 0 && /[A-Za-z0-9_.]/.test(text[j - 1]!)) j -= 1;
      stack.push({ fn: text.slice(j, i).toUpperCase(), argIndex: 0, argStart: i + 1, args: [] });
      i += 1;
      continue;
    }

    if (ch === ')') {
      stack.pop();
      i += 1;
      continue;
    }

    if (ch === ',') {
      const top = stack[stack.length - 1];
      if (top) {
        top.args.push(text.slice(top.argStart, i));
        top.argIndex += 1;
        top.argStart = i + 1;
      } else {
        topArgStart = i + 1;
      }
      i += 1;
      continue;
    }

    i += 1;
  }

  const top = stack[stack.length - 1];
  return {
    fn: top?.fn ?? '',
    argIndex: top?.argIndex ?? 0,
    argStart: top?.argStart ?? topArgStart,
    quoted,
    quoteStart,
    args: top?.args ?? [],
    after: top ? argsAfter(text, caret, quoted) : [],
  };
}

/** The arguments after the caret's own, in the same call — see `CallContext.after`. */
function argsAfter(text: string, caret: number, quotedAtCaret: boolean): string[] {
  const out: string[] = [];
  let quoted = quotedAtCaret;
  let depth = 0;
  let start = -1; // -1 while still in the caret's own argument
  for (let i = caret; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') i += 1;
        else quoted = false;
      }
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === '(') depth += 1;
    else if (ch === ')') {
      if (depth === 0) {
        if (start >= 0) out.push(text.slice(start, i));
        return out;
      }
      depth -= 1;
    } else if (ch === ',' && depth === 0) {
      if (start >= 0) out.push(text.slice(start, i));
      start = i + 1;
    }
  }
  if (start >= 0) out.push(text.slice(start));
  return out;
}

/** What a given argument of a given function wants. */
export type ArgRole = 'field' | 'filter' | 'source' | 'value' | 'other';

interface ArgSpec {
  roles: ArgRole[];
  /**
   * Arguments past the end of `roles` cycle back to this index, for the
   * functions that take an unbounded run of the same shape. `GI.WHERE` is
   * field/value pairs with no limit, so both roles repeat from 0 — without
   * this, a fifth argument falls through to `other` and the field list that
   * was there for the first pair silently stops appearing.
   */
  repeatFrom?: number;
}

const GI_ARGS: Record<string, ArgSpec> = table({
  'GI.TOTAL': ['field', 'filter', 'source'],
  'GI.SUM': ['field', 'filter', 'source'],
  'GI.SUMEACH': ['other', 'filter', 'source'],
  'GI.AVGEACH': ['other', 'filter', 'source'],
  'GI.MINEACH': ['other', 'filter', 'source'],
  'GI.MAXEACH': ['other', 'filter', 'source'],
  'GI.EACH': ['other', 'other', 'filter', 'source'],
  'GI.AVG': ['field', 'filter', 'source'],
  'GI.MIN': ['field', 'filter', 'source'],
  'GI.MAX': ['field', 'filter', 'source'],
  'GI.AREA': ['filter', 'source'],
  'GI.FOOTPRINT': ['filter', 'source'],
  'GI.SITEAREA': [],
  'GI.WEIGHTEDAVG': ['field', 'filter', 'source'],
  'GI.COUNT': ['filter', 'source'],
  'GI.COUNTUNIQUE': ['field', 'filter', 'source'],
  'GI.SUMPRODUCT': ['field', 'field', 'filter', 'source'],
  'GI.NTH': ['field', 'other', 'filter', 'source'],
  'GI.LIST': ['field', 'other', 'filter', 'source', 'other'],
  'GI.FIELDS': ['other', 'source'],
  'GI.CLASS': ['field', 'source'],
  'GI.VALUE': ['field', 'filter', 'source'],
  'GI.UNITS': [],
});

// Pairs, repeating: GI.WHERE("usage", A2, "levels>", 3, …).
GI_ARGS['GI.WHERE'] = { roles: ['field', 'value'], repeatFrom: 0 };

/** The common case — a fixed run of roles, one per argument. */
function table(roles: Record<string, ArgRole[]>): Record<string, ArgSpec> {
  const out: Record<string, ArgSpec> = {};
  for (const [fn, list] of Object.entries(roles)) out[fn] = { roles: list };
  return out;
}

export function argRole(ctx: CallContext): ArgRole {
  return roleAt(ctx.fn, ctx.argIndex);
}

function roleAt(fn: string, argIndex: number): ArgRole {
  const spec = GI_ARGS[fn];
  if (!spec) return 'other';
  const fixed = spec.roles[argIndex];
  if (fixed) return fixed;
  const from = spec.repeatFrom;
  if (from === undefined || argIndex < spec.roles.length) return 'other';
  const period = spec.roles.length - from;
  if (period <= 0) return 'other';
  return spec.roles[from + ((argIndex - from) % period)] ?? 'other';
}

/**
 * The field a `value` argument belongs to — the one named in the argument
 * before it. `GI.WHERE("levels>", 3)` glues the operator onto the field name,
 * so it comes off again here: the values to offer are the values of `levels`.
 */
export function fieldForValueArg(ctx: CallContext): string {
  const raw = ctx.args[ctx.argIndex - 1] ?? '';
  return raw
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(/[=!<>~]+$/, '')
    .trim();
}

export const SOURCE_NAMES = ['areas', 'bakedSections', 'rawSections', 'usages'] as const;
export type SourceName = (typeof SOURCE_NAMES)[number];

/**
 * The source this call names in its own source argument, or null when it
 * names none — so completing `GI.TOTAL("x","","usages")` offers usage fields
 * rather than Areas fields, and the dropdown can say which it is reading.
 */
export function namedSource(ctx: CallContext): SourceName | null {
  if (!GI_ARGS[ctx.fn]) return null;
  // Every argument but the one being typed: those before it, and those after.
  const others: [number, string][] = [
    ...ctx.args.map((a, i): [number, string] => [i, a]),
    ...ctx.after.map((a, i): [number, string] => [ctx.argIndex + 1 + i, a]),
  ];
  for (const [i, text] of others) {
    if (roleAt(ctx.fn, i) !== 'source') continue;
    const raw = text.trim().replace(/^["']|["']$/g, '').toLowerCase();
    const hit = SOURCE_NAMES.find((n) => n.toLowerCase() === raw);
    if (hit) return hit;
  }
  return null;
}

/** Which source is this call reading? Whatever it names, else the Areas rows GI.* reads by default. */
export function sourceOf(ctx: CallContext): SourceName {
  return namedSource(ctx) ?? 'areas';
}

/**
 * How to write this function with its source named — `GI.TOTAL(field, "", "bakedSections")` —
 * or null for a function with no source argument (GI.WHERE, GI.UNITS).
 */
export function sourceExample(fn: string, source: SourceName): string | null {
  const roles = GI_ARGS[fn]?.roles ?? [];
  const at = roles.indexOf('source');
  if (at < 0) return null;
  const args = roles.slice(0, at + 1).map((r) => (r === 'field' ? 'field' : r === 'filter' ? '""' : r === 'source' ? `"${source}"` : '…'));
  return `${fn}(${args.join(', ')})`;
}

/**
 * Split a partially-typed filter into the clause under the caret. A filter is
 * comma-separated (`usage=Residential, levels>3`), so completion applies to the
 * last clause only.
 *
 * Hand-split rather than regex-matched: a single pattern with an optional
 * operator lets the field group match empty and swallows the whole clause into
 * `value`, which silently turns every filter completion into a field list.
 */
export function filterClauseAt(argText: string): {
  /** Everything before the clause under the caret, kept verbatim. */
  prefix: string;
  field: string;
  op: string;
  value: string;
} {
  const cut = argText.lastIndexOf(',');
  const head = cut >= 0 ? argText.slice(0, cut + 1) : '';
  const clause = cut >= 0 ? argText.slice(cut + 1) : argText;

  const lead = /^\s*/.exec(clause)![0];
  const body = clause.slice(lead.length);
  const prefix = head + lead;

  const opAt = body.search(/[!=<>~]/);
  if (opAt < 0) return { prefix, field: body.trim(), op: '', value: '' };

  const two = body.slice(opAt, opAt + 2);
  const op = ['!=', '>=', '<='].includes(two) ? two : body[opAt]!;
  return {
    prefix,
    field: body.slice(0, opAt).trim(),
    op,
    value: body.slice(opAt + op.length),
  };
}
